/**
 * Estado dos avisos de conflito de agenda devolvidos ao criar/editar um plantao.
 *
 * O backend detecta a sobreposicao (`checkDoctorOverlap`) dentro da propria transacao
 * de escrita e devolve `overlapAlerts` junto do plantao ja gravado. Logo, este estado
 * so e preenchido DEPOIS do sucesso da mutacao: e um aviso, nunca um bloqueio.
 * O bloqueio duro do painel continua sendo apenas o limite de horas consecutivas
 * (`useConsecutiveHoursCheck`).
 */
import { ref, computed } from 'vue'
import type { Alert, StationMutationResponse } from '@/Network/Types/Responses/StationResponses'

function readOverlapAlerts(response: unknown): Alert[] {
  if (!response || typeof response !== 'object') return []
  const raw = (response as StationMutationResponse).overlapAlerts
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (alert): alert is Alert => !!alert && typeof alert === 'object' && Boolean(alert.isAlert)
  )
}

export function useStationOverlapAlerts() {
  const alerts = ref<Alert[]>([])

  const hasAlerts = computed(() => alerts.value.length > 0)

  function clear() {
    alerts.value = []
  }

  /** `true` quando a resposta trouxe conflito de agenda (e o bloco de aviso foi preenchido). */
  function applyFromResponse(response: unknown): boolean {
    alerts.value = readOverlapAlerts(response)
    return alerts.value.length > 0
  }

  return { alerts, hasAlerts, applyFromResponse, clear }
}
